import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useI18n, SUPPORTED_LANGUAGES } from "../../contexts/I18nContext";
import { Globe, Check, ChevronDown } from "lucide-react";

/**
 * ONTHEPITCH language switcher (v4 — production-bulletproof).
 *  - Portals to <body> with position: fixed (never clipped by parents).
 *  - 100% SOLID background — NO backdrop-filter dependency (some
 *    browsers/extensions disable filter, which was rendering an empty
 *    black panel). Background is a fully opaque #0F1316.
 *  - No opacity animation that can race on remount.
 *  - Auto-flips upward when trigger is near viewport bottom.
 *  - Closes on outside click, Escape, scroll, resize, route change.
 *  - Works on mobile (touchstart) + keyboard navigation.
 */
export default function LanguageToggle({ className = "", align = "right" }) {
  const { lang, changeLang } = useI18n();
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({ left: 0, top: 0, dropUp: false, width: 220 });
  const wrapRef = useRef(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  const MENU_W = 220;
  const ITEM_H = 46;
  const PAD = 14;

  const computePos = () => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const menuH = SUPPORTED_LANGUAGES.length * ITEM_H + PAD;
    const dropUp = window.innerHeight - rect.bottom < menuH + 12;
    // Use viewport width to clamp; on mobile, take 90vw if too narrow
    const vw = window.innerWidth;
    const width = Math.min(MENU_W, vw - 16);
    let left;
    if (align === "left") {
      left = rect.left;
    } else {
      left = rect.right - width;
    }
    if (left < 8) left = 8;
    if (left + width > vw - 8) left = vw - width - 8;
    const top = dropUp ? rect.top - menuH - 8 : rect.bottom + 8;
    setCoords({ left, top, dropUp, width });
  };

  useLayoutEffect(() => { if (open) computePos(); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (wrapRef.current?.contains(e.target)) return;
      if (menuRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    const onReflow = () => computePos();
    document.addEventListener("mousedown", onClick);
    document.addEventListener("touchstart", onClick, { passive: true });
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onReflow, true);
    window.addEventListener("resize", onReflow);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("touchstart", onClick);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onReflow, true);
      window.removeEventListener("resize", onReflow);
    };
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const current = SUPPORTED_LANGUAGES.find((l) => l.code === lang) || SUPPORTED_LANGUAGES[0];

  const pick = async (code) => {
    setOpen(false);
    if (code !== lang) await changeLang(code);
  };

  return (
    <div ref={wrapRef} className={`relative inline-block ${className}`} data-testid="language-toggle">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex items-center gap-2 border px-3 py-2 text-[11px] font-mono font-medium tracking-widest rounded-md transition-all duration-200 ${
          open ? "border-pitch bg-pitch/10 text-white shadow-[0_0_0_2px_rgba(59,130,246,0.12)]"
               : "border-white/15 hover:border-pitch/55 hover:bg-white/[0.03] text-white"
        }`}
        data-testid="language-toggle-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`Change language. Current: ${current.label}`}
      >
        <Globe className="w-3.5 h-3.5 text-pitch" />
        <span className="leading-none">{current.short}</span>
        <ChevronDown className={`w-3 h-3 transition-transform duration-200 ${open ? "rotate-180 text-pitch" : "text-white/55"}`} />
      </button>

      {open && typeof document !== "undefined" && createPortal(
        <div
          ref={menuRef}
          role="listbox"
          aria-label="Language"
          data-testid="language-menu"
          data-direction={coords.dropUp ? "up" : "down"}
          style={{
            position: "fixed",
            left: coords.left,
            top: coords.top,
            width: coords.width,
            zIndex: 2147483647,
            opacity: 1,
            // 100% SOLID background — no backdrop filter dependency
            background: "#0F1316",
            border: "1px solid rgba(59,130,246, 0.22)",
            borderRadius: 12,
            padding: 6,
            boxShadow:
              "0 24px 60px -16px rgba(0, 0, 0, 0.85), " +
              "0 0 0 1px rgba(59,130,246, 0.08) inset, " +
              "0 8px 24px -8px rgba(59,130,246, 0.18)",
            display: "flex",
            flexDirection: "column",
            gap: 2,
            animation: "otpFadeInUp 140ms ease-out",
          }}
        >
          {SUPPORTED_LANGUAGES.map((l) => {
            const active = l.code === lang;
            return (
              <button
                key={l.code}
                type="button"
                onClick={() => pick(l.code)}
                data-testid={`lang-${l.code}`}
                role="option"
                aria-selected={active}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 10,
                  padding: "11px 12px",
                  width: "100%",
                  minHeight: 44,
                  background: active ? "rgba(59,130,246, 0.14)" : "transparent",
                  border: 0,
                  color: active ? "#3B82F6" : "#ffffff",
                  borderRadius: 8,
                  cursor: "pointer",
                  fontFamily: "inherit",
                  transition: "background-color 0.15s, color 0.15s",
                  outline: "none",
                }}
                onMouseEnter={(e) => {
                  if (!active) e.currentTarget.style.background = "rgba(255, 255, 255, 0.06)";
                  e.currentTarget.style.color = active ? "#3B82F6" : "#ffffff";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = active ? "rgba(59,130,246, 0.14)" : "transparent";
                  e.currentTarget.style.color = active ? "#3B82F6" : "#ffffff";
                }}
                onFocus={(e) => {
                  if (!active) e.currentTarget.style.background = "rgba(255, 255, 255, 0.06)";
                }}
                onBlur={(e) => {
                  e.currentTarget.style.background = active ? "rgba(59,130,246, 0.14)" : "transparent";
                }}
              >
                <span style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <span style={{ fontSize: 20, lineHeight: 1 }}>{l.flag}</span>
                  <span style={{ fontFamily: "'Oswald', sans-serif", fontWeight: 500, textTransform: "uppercase", letterSpacing: "-0.01em", fontSize: 14 }}>
                    {l.label}
                  </span>
                </span>
                {active && <Check style={{ width: 16, height: 16, color: "#3B82F6", flexShrink: 0 }} />}
              </button>
            );
          })}
        </div>,
        document.body,
      )}
    </div>
  );
}
